import {
  DefectCatalogItem,
  toDefectMeasurementSlug,
  toDefectSlug,
  toDefectSurveyYearSlug,
} from '../contracts/availableLayers';
import {
  PAS2161_VALUE_RAG_COLOR_SCHEME,
  PAS2161_VALUE_RAG_THRESHOLDS,
} from './pas2161Bands';
import {
  formatPas2161RegionClientLabel,
  PAS2161_CLIENTS,
  PAS2161_MFV_LAYER_NAMES,
  PAS2161_MINI_DEFECT_META,
  PAS2161_MINI_DEFECTS,
  PAS2161_REGIONS,
  PAS2161_SURVEY_YEAR,
  PAS2161_WORKSPACE,
  formatPas2161LayerId,
  pas2161RunsForRegion,
  toPas2161MiniLayerName,
  type Pas2161Client,
  type Pas2161LayerName,
  type Pas2161LayerRef,
  type Pas2161MfvLayerName,
  type Pas2161Region,
  type Pas2161Run,
  type Pas2161SurveyType,
} from './pas2161LayerModel';

const SURVEY_YEAR_START = '2026-04-01';
const SURVEY_YEAR_END = '2027-03-31';

function buildWfsUrl(sourceLayerId: string): string {
  return `https://geoserver.xais.co.uk/geoserver/wfs_layers/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=${encodeURIComponent(sourceLayerId)}`;
}

function mfvLayerMeta(layerName: Pas2161MfvLayerName): {
  readonly name: string;
  readonly catalogCode: string;
  readonly datasetLabel: string;
} {
  if (layerName === 'accred-defect-grid') {
    return {
      name: 'Cracking (Accredited)',
      catalogCode: 'ACCRCK',
      datasetLabel: 'accred',
    };
  }
  return {
    name: 'Cracking (Relaxed)',
    catalogCode: 'RELCRCK',
    datasetLabel: 'relaxed',
  };
}

function buildRawRef(
  client: Pas2161Client,
  surveyType: Pas2161SurveyType,
  run: Pas2161Run,
  layerName: Pas2161LayerName,
): Pas2161LayerRef {
  return {
    workspace: PAS2161_WORKSPACE,
    client,
    surveyType,
    run,
    layerName,
    aggregationM: null,
    surveyYear: PAS2161_SURVEY_YEAR,
  };
}

function buildSurveyYearRows(
  client: Pas2161Client,
  region: Pas2161Region,
  surveyType: Pas2161SurveyType,
  layerName: Pas2161LayerName,
) {
  return pas2161RunsForRegion(region).map((run) => {
    const ref = buildRawRef(client, surveyType, run, layerName);
    const sourceLayerId = formatPas2161LayerId(ref);
    const pass = run.slice(region.length + 1);
    return {
      id: `${client}-${run}-${PAS2161_SURVEY_YEAR}`,
      slug: toDefectSurveyYearSlug(`${client}-${run}-${PAS2161_SURVEY_YEAR}`),
      surveyYear: `${PAS2161_SURVEY_YEAR} · ${pass}`,
      startDate: SURVEY_YEAR_START,
      endDate: SURVEY_YEAR_END,
      sourceLayerId,
      vectorSourceUrl: buildWfsUrl(sourceLayerId),
      lanes: ['all'] as const,
    };
  });
}

function buildMiniDefect(
  client: Pas2161Client,
  region: Pas2161Region,
  defectCode: (typeof PAS2161_MINI_DEFECTS)[number],
): DefectCatalogItem {
  const meta = PAS2161_MINI_DEFECT_META[defectCode];
  const layerName = toPas2161MiniLayerName(defectCode);
  const label = formatPas2161RegionClientLabel(region);

  return {
    id: `pas2161-${client}-${region}-m-mfv-${defectCode}`,
    slug: toDefectSlug(`${client}-${region}-m-mfv-${defectCode}`),
    surveyType: 'm-mfv',
    name: `${label} Mini MFV – ${meta.name}`,
    code: meta.catalogCode,
    type: 'Road Surface',
    presentation: meta.presentation,
    measurements: [
      {
        name: meta.name,
        slug: toDefectMeasurementSlug(
          `pas2161-${region}-${defectCode}-overlay`,
        ),
        code: `${meta.catalogCode}O`,
        description: `${label} Mini MFV ${meta.name.toLowerCase()}. Point VALUE attributes; grid overview when zoomed out; full WFS at street zoom. Survey years are main / repeat1 / repeat2.`,
        parameter: defectCode,
        colorScheme: PAS2161_VALUE_RAG_COLOR_SCHEME,
        thresholds: [...PAS2161_VALUE_RAG_THRESHOLDS],
        surveyYears: buildSurveyYearRows(client, region, 'm-mfv', layerName),
      },
    ],
  };
}

function buildMfvDefect(
  client: Pas2161Client,
  region: Pas2161Region,
  layerName: Pas2161MfvLayerName,
): DefectCatalogItem {
  const meta = mfvLayerMeta(layerName);
  const label = formatPas2161RegionClientLabel(region);
  return {
    id: `pas2161-${client}-${region}-mfv-${meta.datasetLabel}`,
    slug: toDefectSlug(`${client}-${region}-mfv-${meta.datasetLabel}-cracking`),
    surveyType: 'mfv',
    name: `${label} MFV – ${meta.name}`,
    code: meta.catalogCode,
    type: 'Road Surface',
    presentation: 'crackmap',
    measurements: [
      {
        name: meta.name,
        slug: toDefectMeasurementSlug(
          `pas2161-${region}-mfv-${meta.datasetLabel}-overlay`,
        ),
        code: `${meta.catalogCode}O`,
        description: `${label} MFV ${meta.name.toLowerCase()}. Point VALUE attributes; grid overview when zoomed out; full WFS at street zoom. Survey years are main / repeat1 / repeat2.`,
        parameter: layerName,
        colorScheme: PAS2161_VALUE_RAG_COLOR_SCHEME,
        thresholds: [...PAS2161_VALUE_RAG_THRESHOLDS],
        surveyYears: buildSurveyYearRows(client, region, 'mfv', layerName),
      },
    ],
  };
}

export function isPas2161CatalogEnabled(): boolean {
  return process.env.REACT_APP_PAS2161_WFS_ENABLED !== 'false';
}

/**
 * 24 catalog defects: region × (6 mini + 2 mfv).
 * Passes (main / repeat1 / repeat2) are survey-year rows (×3 per defect).
 */
export function getPas2161CatalogDefects(): readonly DefectCatalogItem[] {
  if (!isPas2161CatalogEnabled()) {
    return [];
  }

  const defects: DefectCatalogItem[] = [];
  for (const client of PAS2161_CLIENTS) {
    for (const region of PAS2161_REGIONS) {
      for (const defectCode of PAS2161_MINI_DEFECTS) {
        defects.push(buildMiniDefect(client, region, defectCode));
      }
      for (const layerName of PAS2161_MFV_LAYER_NAMES) {
        defects.push(buildMfvDefect(client, region, layerName));
      }
    }
  }
  return defects;
}
